// Org risk trend chart with industry baseline overlay. Org series is
// solid; industry baseline (when industryBenchmarking is on) is dashed.
//
// Data: real daily org-score history from the risk_trend aggregation
// (tenancy_risk_org_daily, written by the refresh worker; historical gap
// fillable with reportsb-refresh/backfill.py). No synthetic fallback -
// with under 3 days of history the card shows a collecting-history note,
// never an invented curve.
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../../components/cards/MainCard';
import ReactApexChart from 'react-apexcharts';
import { motion, AnimatePresence } from 'framer-motion';
import { BENCHMARK } from '../../riskScore';
import useFeature from '../../useFeature';
import useDashboardData from '../../hooks/useDashboardData';

// Org leads in red; the peer baselines trail as dashed lines. Platform is
// the always-available cross-tenant average; industry is the licensed
// peer-group overlay layered on top of it.
const ORG_COLOR = '#FF5252';
const PLATFORM_COLOR = '#6B7591';
const INDUSTRY_COLOR = '#00BFA5';

export default function RiskTrendChart() {
    const theme = useTheme();
    const gridColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
    const benchmarkOn = useFeature('industryBenchmarking');
    const [simple, setSimple] = useState(() => localStorage.getItem('rpt_risktrend_view') === 'simple');
    // Real daily org-score history from the rollup. Under 3 days of history the
    // card shows a collecting-history note instead of a chart.
    const trendQ = useDashboardData({ metric: 'simulations', aggregation: 'risk_trend' });
    const aggTrend = trendQ.data?.riskTrend;
    const hasTrend = Array.isArray(aggTrend) && aggTrend.length >= 3;
    const trend = hasTrend
        ? aggTrend.map((p) => ({ label: p.day.slice(5), org: p.score, industry: null, platform: null }))
        : [];

    // Honest span label from the actual data range, e.g. "22 Jul - 12 Aug".
    const fmtDay = (iso) => {
        const d = new Date(`${iso}T00:00:00Z`);
        return `${d.getUTCDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()]}`;
    };
    const spanLabel = hasTrend ? `${fmtDay(aggTrend[0].day)} - ${fmtDay(aggTrend[aggTrend.length - 1].day)}` : null;

    const MONTHS_LABELS = trend.map((p) => p.label);
    const ORG_SERIES = trend.map((p) => p.org);
    const INDUSTRY_SERIES = trend.map((p) => p.industry);
    const PLATFORM_SERIES = trend.map((p) => p.platform);
    const hasPlatform = PLATFORM_SERIES.some((v) => v != null);
    const hasIndustry = INDUSTRY_SERIES.some((v) => v != null);
    const showPlatform = benchmarkOn && hasPlatform;
    const showIndustry = benchmarkOn && hasIndustry;

    // When benchmarking is licensed the org line is trailed by the platform
    // and industry baselines (only the ones with data); without it the chart
    // is org-only. Platform/industry are kept in the series (width 0 = not
    // drawn) so the shared tooltip still reports their values on hover.
    const lineColors = [ORG_COLOR, ...(showPlatform ? [PLATFORM_COLOR] : []), ...(showIndustry ? [INDUSTRY_COLOR] : [])];
    const dashArray = [0];
    const strokeWidth = [3, ...(showPlatform ? [0] : []), ...(showIndustry ? [0] : [])];
    const benchSeries = [
        ...(showPlatform ? PLATFORM_SERIES : []),
        ...(showIndustry ? INDUSTRY_SERIES : []),
    ].filter((v) => v != null);

    const yMin = Math.max(0, Math.min(...ORG_SERIES, ...benchSeries) - 10);
    const yMax = Math.min(100, Math.max(...ORG_SERIES, ...benchSeries) + 10);

    const detailedOptions = {
        chart: {
            type: 'line',
            toolbar: { show: false },
            animations: { enabled: false },
            background: 'transparent',
            sparkline: { enabled: false },
        },
        stroke: { curve: 'smooth', width: strokeWidth, dashArray },
        colors: lineColors,
        // Single (non-array) fill.type: only the org series is an area, and
        // line series carry no fill region, so the gradient applies just to
        // the org band. Passing an array here triggers an ApexCharts combo
        // regression (>=3.36) that drops the trailing line series entirely.
        fill: {
            type: 'gradient',
            gradient: {
                type: 'vertical',
                colorStops: [
                    { offset: 0,   color: '#FF6B7A', opacity: 0.22 },
                    { offset: 50,  color: '#FF5252', opacity: 0.08 },
                    { offset: 100, color: '#FF5252', opacity: 0 },
                ],
            },
        },
        markers: {
            size: lineColors.map(() => 0),
            hover: { size: 5 },
            colors: lineColors,
            strokeColors: theme.palette.background.paper,
            strokeWidth: 2,
            fillOpacity: 1,
        },
        xaxis: {
            categories: MONTHS_LABELS,
            labels: {
                style: { fontSize: '10px', fontFamily: 'Inter', colors: theme.palette.text.primary },
                rotate: 0,
                hideOverlappingLabels: true,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            min: yMin, max: yMax,
            labels: {
                offsetX: -14,
                style: { fontSize: '10px', fontFamily: 'Inter', colors: theme.palette.text.primary },
            },
        },
        grid: { borderColor: gridColor, xaxis: { lines: { show: false } }, padding: { left: 4, right: 4, bottom: 0 } },
        tooltip: {
            theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
            style: { fontSize: '11px', fontFamily: 'Inter' },
            shared: true,
        },
        legend: { show: false },
    };

    const simpleOptions = {
        chart: {
            type: 'area',
            toolbar: { show: false },
            animations: { enabled: false },
            background: 'transparent',
            sparkline: { enabled: true },
        },
        stroke: { curve: 'smooth', width: 2.5 },
        colors: ['#FF5252'],
        fill: {
            type: 'gradient',
            gradient: {
                type: 'vertical',
                colorStops: [[
                    { offset: 0,   color: '#FF6B7A', opacity: 0.28 },
                    { offset: 100, color: '#FF5252', opacity: 0 },
                ]],
            },
        },
        tooltip: {
            enabled: true,
            theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
            style: { fontSize: '11px', fontFamily: 'Inter' },
        },
        markers: {
            size: 0,
            hover: { size: 5 },
            colors: ['#FF5252'],
            strokeColors: theme.palette.background.paper,
            strokeWidth: 2,
            fillOpacity: 1,
        },
        xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { show: false, min: yMin, max: yMax },
        grid: { show: false, padding: { left: 0, right: 0, top: 0, bottom: 0 } },
        legend: { show: false },
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            onAnimationComplete={() => requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))}
            style={{ height: '100%' }}
        >
            <MainCard content={false} sx={{ bgcolor: 'background.paper', height: '100%', minHeight: 260, overflow: 'hidden' }} border={false} boxShadow={true}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 260 }}>
                    {/* Header, always padded, always visible */}
                    <Box sx={{ px: 3, pt: 3, pb: 1.25, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pr: simple ? 3 : 'calc(24px + 18px)' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Risk score trend
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                {(() => {
                                    if (!hasTrend) return 'Daily org score history';
                                    const bits = [];
                                    if (showPlatform && BENCHMARK.platformAvg != null) bits.push(`platform (${BENCHMARK.platformAvg})`);
                                    if (showIndustry && BENCHMARK.industryAvg != null) bits.push(`industry (${BENCHMARK.industryAvg})`);
                                    return bits.length ? `${spanLabel} - org vs ${bits.join(' & ')}` : `${spanLabel} - your org score`;
                                })()}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {!simple && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                        <Box sx={{ width: 20, height: 2, bgcolor: ORG_COLOR, borderRadius: '1px', flexShrink: 0 }} />
                                        Org
                                    </Box>
                                    {/* Platform/industry are tooltip-only series (no drawn
                                        line), so the legend keys them with a dot, not a line. */}
                                    {showPlatform && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PLATFORM_COLOR, flexShrink: 0 }} />
                                            Platform
                                        </Box>
                                    )}
                                    {showIndustry && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: INDUSTRY_COLOR, flexShrink: 0 }} />
                                            Industry
                                        </Box>
                                    )}
                                </>
                            )}
                            <ToggleButton simple={simple} isDark={theme.palette.mode === 'dark'} onToggle={() => { setSimple(s => { const next = !s; localStorage.setItem('rpt_risktrend_view', next ? 'simple' : 'detailed'); return next; }); requestAnimationFrame(() => window.dispatchEvent(new Event('resize'))); }} />
                        </Box>
                    </Box>

                    {/* Chart area, padded in detailed view, edge-to-edge in simple */}
                    {!hasTrend ? (
                        <Box sx={{ flex: 1, minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 3, pb: 3, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                                Collecting daily history
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled', maxWidth: 320 }}>
                                Your organisation score is recorded once a day. The trend appears after 3 days of scored activity.
                            </Typography>
                        </Box>
                    ) : (
                    <Box sx={{ flex: 1, minHeight: 180, px: simple ? 0 : 3, pb: simple ? 0 : 3 }}>
                        <Box sx={{ position: 'relative', height: '100%' }}>
                            <AnimatePresence mode="sync" initial={false}>
                                {simple ? (
                                    <motion.div
                                        key="simple"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.18 }}
                                        style={{ position: 'absolute', inset: 0 }}
                                    >
                                        <ReactApexChart
                                            options={simpleOptions}
                                            series={[{ name: 'Org', data: ORG_SERIES, type: 'area' }]}
                                            type="area"
                                            height="100%"
                                            width="100%"
                                        />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="detailed"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.18 }}
                                        style={{ position: 'absolute', inset: 0 }}
                                    >
                                        <ReactApexChart
                                            options={{ ...detailedOptions, chart: { ...detailedOptions.chart, height: '100%' } }}
                                            series={[
                                                { name: 'Org', data: ORG_SERIES, type: 'area' },
                                                ...(showPlatform ? [{ name: 'Platform', data: PLATFORM_SERIES, type: 'line' }] : []),
                                                ...(showIndustry ? [{ name: 'Industry', data: INDUSTRY_SERIES, type: 'line' }] : []),
                                            ]}
                                            type="line"
                                            height="100%"
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Box>
                    </Box>
                    )}
                </Box>
            </MainCard>
        </motion.div>
    );
}

function ToggleButton({ simple, isDark, onToggle }) {
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
    const strongColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)';
    const borderColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)';
    const bgColor = isDark ? '#333537' : '#ffffff';

    return (
        <Box
            component="button"
            onClick={onToggle}
            sx={{
                display: 'flex', alignItems: 'center', gap: '5px',
                px: '8px', py: '3px',
                border: `1px solid ${borderColor}`,
                borderRadius: '5px',
                background: bgColor,
                cursor: 'pointer',
                color: mutedColor,
                fontSize: 10,
                fontFamily: 'Inter, sans-serif',
                transition: 'border-color 150ms, color 150ms',
                '&:hover': { borderColor: mutedColor, color: strongColor },
            }}
        >
            {simple ? (
                <>
                    <Box component="span" sx={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: 10 }}>
                        {[4,7,5,9,6,8,7,10].map((h, i) => (
                            <Box key={i} sx={{ width: 2, height: h, bgcolor: 'currentColor', borderRadius: '1px' }} />
                        ))}
                    </Box>
                    Detail
                </>
            ) : (
                <>
                    <Box component="span" sx={{ width: 16, height: 10, position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ position: 'absolute', width: '100%', height: '2px', bgcolor: 'currentColor', borderRadius: '1px', top: '30%' }} />
                        <Box sx={{ position: 'absolute', width: '100%', height: '2px', bgcolor: 'currentColor', borderRadius: '1px', opacity: 0.4, top: '70%' }} />
                    </Box>
                    Simple
                </>
            )}
        </Box>
    );
}
